import { useState, useEffect } from 'react';
import { Button, Input, Modal, Table, Card, useToast } from '../components/common';
import api from '../services/api';
import type { JinaKey } from '../types';

interface JinaKeyFormData {
  key_value: string;
  key_type: 'free' | 'paid';
  daily_quota: string;
}

export default function JinaKeysPage() {
  const { addToast } = useToast();
  const [jinaKeys, setJinaKeys] = useState<JinaKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<JinaKeyFormData>({ key_value: '', key_type: 'free', daily_quota: '' });
  const [deleteConfirm, setDeleteConfirm] = useState<JinaKey | null>(null);

  useEffect(() => { fetchJinaKeys(); }, []);

  const fetchJinaKeys = async () => {
    try {
      setLoading(true);
      const response = await api.get<JinaKey[]>('/api/v1/jina-keys');
      setJinaKeys(response.data);
    } catch (error) {
      addToast('error', '無法載入 Jina Key 列表');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/v1/jina-keys', {
        key_value: formData.key_value,
        key_type: formData.key_type,
        daily_quota: formData.daily_quota ? parseInt(formData.daily_quota) : null
      });
      addToast('success', 'Jina Key 已新增');
      setShowModal(false);
      setFormData({ key_value: '', key_type: 'free', daily_quota: '' });
      fetchJinaKeys();
    } catch (error: any) {
      addToast('error', error.response?.data?.error?.message || '新增失敗');
    }
  };

  const handleToggleStatus = async (key: JinaKey) => {
    try {
      await api.put(`/api/v1/jina-keys/${key.id}`, { is_active: !key.is_active });
      addToast('success', `Jina Key 已${key.is_active ? '停用' : '啟用'}`);
      fetchJinaKeys();
    } catch (error: any) {
      addToast('error', error.response?.data?.error?.message || '操作失敗');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/api/v1/jina-keys/${deleteConfirm.id}`);
      addToast('success', 'Jina Key 已刪除');
      setDeleteConfirm(null);
      fetchJinaKeys();
    } catch (error: any) {
      addToast('error', error.response?.data?.error?.message || '刪除失敗');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    addToast('success', '已複製到剪貼簿');
  };

  const columns = [
    { key: 'key_value', header: 'Jina Key', render: (k: JinaKey) => (
      <div className="flex items-center gap-2">
        <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded max-w-xs truncate">{k.key_value}</code>
        <button onClick={() => copyToClipboard(k.key_value)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" title="複製">📋</button>
      </div>
    )},
    { key: 'key_type', header: '類型', render: (k: JinaKey) => (
      <span className={`px-2 py-1 rounded text-xs ${k.key_type === 'paid' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}`}>
        {k.key_type === 'paid' ? '付費' : '免費'}
      </span>
    )},
    { key: 'daily_quota', header: '每日配額', render: (k: JinaKey) => k.daily_quota || '無限制' },
    { key: 'total_requests', header: '使用次數' },
    { key: 'is_active', header: '狀態', render: (k: JinaKey) => (
      <span className={`px-2 py-1 rounded text-xs ${k.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
        {k.is_active ? '啟用' : '停用'}
      </span>
    )},
    { key: 'last_used_at', header: '最後使用', render: (k: JinaKey) => k.last_used_at ? new Date(k.last_used_at).toLocaleString('zh-TW') : '-' },
    { key: 'actions', header: '操作', render: (k: JinaKey) => (
      <div className="flex gap-2">
        <Button size="sm" variant={k.is_active ? 'danger' : 'primary'} onClick={() => handleToggleStatus(k)}>{k.is_active ? '停用' : '啟用'}</Button>
        <Button size="sm" variant="danger" onClick={() => setDeleteConfirm(k)}>刪除</Button>
      </div>
    )},
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Jina Key 管理</h1>
        <Button onClick={() => setShowModal(true)}>新增 Jina Key</Button>
      </div>

      <Card>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <Table data={jinaKeys} columns={columns} keyExtractor={(k) => k.id} emptyMessage="暫無 Jina Key" />
        )}
      </Card>

      {/* Create Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="新增 Jina Key">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input label="Jina Key" value={formData.key_value} onChange={(e) => setFormData({ ...formData, key_value: e.target.value })} required placeholder="jina_..." />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">類型</label>
            <select value={formData.key_type} onChange={(e) => setFormData({ ...formData, key_type: e.target.value as 'free' | 'paid' })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              <option value="free">免費</option>
              <option value="paid">付費</option>
            </select>
          </div>
          <Input label="每日配額（選填）" type="number" value={formData.daily_quota} onChange={(e) => setFormData({ ...formData, daily_quota: e.target.value })} placeholder="留空表示無限制" />
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>取消</Button>
            <Button type="submit">新增</Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="確認刪除">
        <p className="text-gray-600 dark:text-gray-400 mb-4">確定要刪除此 Jina Key 嗎？此操作無法復原。</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>取消</Button>
          <Button variant="danger" onClick={handleDelete}>確認刪除</Button>
        </div>
      </Modal>
    </div>
  );
}
